import { useCallback, useEffect, useState } from 'react'

import { useMutation } from '@apollo/client'
import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  GetInternalExpenseViewerEnabledDocument,
  InternalExpenseViewerByIdDocument,
  InternalExpenseViewerFieldsFragment,
  InternalExpenseViewerRandomExpenseDocument,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import InternalExpenseDetail from 'modules/expenses/components/expenses/InternalViewer'

const InternalExpenseViewerPage = () => {
  const { company } = useCurrentSession()
  const companyId = company?.id

  // Check if feature is enabled
  const { data: enabledData, loading: enabledLoading } = useQuery(
    GetInternalExpenseViewerEnabledDocument
  )

  const isEnabled =
    enabledData?.expenses?._policies?.internalExpenseViewerEnabledContextless?.can ?? false

  // Mutation to fetch random expense
  const [fetchRandomExpense, { loading: randomLoading }] = useMutation(
    InternalExpenseViewerRandomExpenseDocument
  )

  // Mutation to fetch expense by ID
  const [fetchExpenseById, { loading: byIdLoading }] = useMutation(
    InternalExpenseViewerByIdDocument
  )

  const [expensable, setExpensable] = useState<InternalExpenseViewerFieldsFragment | null>(null)
  const [goToId, setGoToId] = useState('')
  const [scoping, setScoping] = useState<'reviewed' | 'not_reviewed'>('not_reviewed')
  const [error, setError] = useState<string | null>(null)

  const loading = randomLoading || byIdLoading

  // Fetch initial random expense when enabled
  useEffect(() => {
    if (isEnabled && companyId) {
      setError(null)
      fetchRandomExpense({
        variables: { companyId, excludeId: null, scoping },
      })
        .then((result) => {
          if (result.errors?.length) {
            setError(result.errors.map((e) => e.message).join(', '))
            return
          }
          const newExpensable =
            result.data?.expensesMutations?.readRandomExpensesInternalExpenseViewer
              ?.internalExpenseViewer
          if (newExpensable) {
            setExpensable(newExpensable)
          }
        })
        .catch((err) => {
          setError(err.message || 'Unknown error')
        })
    }
  }, [isEnabled, companyId, scoping, fetchRandomExpense])

  const handleGoToId = useCallback(() => {
    const id = parseInt(goToId, 10)
    if (!isNaN(id) && id > 0 && companyId) {
      fetchExpenseById({
        variables: { companyId, id },
      }).then((result) => {
        const newExpensable =
          result.data?.expensesMutations?.readByIdExpensesInternalExpenseViewer
            ?.internalExpenseViewer
        if (newExpensable) {
          setExpensable(newExpensable)
        }
      })
      setGoToId('')
    }
  }, [goToId, companyId, fetchExpenseById])

  const handleNext = useCallback(() => {
    if (companyId) {
      fetchRandomExpense({
        variables: { companyId, excludeId: expensable?.id ?? null, scoping },
      }).then((result) => {
        const newExpensable =
          result.data?.expensesMutations?.readRandomExpensesInternalExpenseViewer
            ?.internalExpenseViewer
        if (newExpensable) {
          setExpensable(newExpensable)
        }
      })
    }
  }, [expensable?.id, companyId, scoping, fetchRandomExpense])

  if (enabledLoading) {
    return <Loading expand />
  }

  if (!isEnabled) {
    return (
      <Box padding="s24" alignItems="center" justifyContent="center" height="full">
        <Text.Normal color="grey600">
          This feature is not enabled for your company. The
          InternalExpenseViewerEnabledContextlessPolicy must be satisfied.
        </Text.Normal>
      </Box>
    )
  }

  if (loading && !expensable) {
    return <Loading expand />
  }

  if (error) {
    return (
      <Box padding="s24" alignItems="center" justifyContent="center" height="full">
        <Text.Normal color="moodNegative">Error: {error}</Text.Normal>
      </Box>
    )
  }

  if (!expensable) {
    return (
      <Box padding="s24" alignItems="center" justifyContent="center" height="full">
        <Text.Normal color="grey600">No expenses found in this company</Text.Normal>
      </Box>
    )
  }

  return (
    <Box flexDirection="column" height="full">
      <Box
        padding="s16"
        flexDirection="row"
        justifyContent="spaceBetween"
        alignItems="center"
        style={{ borderBottom: '1px solid var(--border-default)' }}
      >
        <Box flexDirection="column">
          <Text.Normal weight="bold">Internal Expense Viewer</Text.Normal>
          <Box flexDirection="row" gap="s4">
            <Text.Normal size="200" color="grey600">
              ID: {expensable.expenseId} | Type: {expensable.type} | Status:
            </Text.Normal>
            <Text.Normal
              size="200"
              color={
                expensable.status === 'rejected'
                  ? 'moodNegative'
                  : ['approved', 'paid', 'in_payroll', 'sent_to_pay'].includes(expensable.status)
                    ? 'moodPositive'
                    : 'grey600'
              }
            >
              {expensable.status}
            </Text.Normal>
          </Box>
        </Box>
        <Box flexDirection="row" alignItems="center" gap="s8">
          <select
            value={scoping}
            onChange={(e) => setScoping(e.target.value as 'reviewed' | 'not_reviewed')}
            style={{
              padding: '8px 12px',
              border: '1px solid #c4c4c4',
              borderRadius: '4px',
              fontSize: '14px',
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="not_reviewed">Not Reviewed</option>
            <option value="reviewed">Reviewed</option>
          </select>
          <input
            type="text"
            placeholder="Expense ID"
            value={goToId}
            onChange={(e) => setGoToId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleGoToId()}
            style={{
              padding: '8px 12px',
              border: '1px solid #c4c4c4',
              borderRadius: '4px',
              width: '120px',
              fontSize: '14px',
              outline: 'none',
            }}
          />
          <Button onClick={handleGoToId} type="secondary">
            GO
          </Button>
          <Button onClick={handleNext} type="primary" loading={randomLoading}>
            NEXT
          </Button>
        </Box>
      </Box>
      <Box style={{ flex: '1', overflow: 'hidden' }}>
        <InternalExpenseDetail expensable={expensable} />
      </Box>
    </Box>
  )
}

export default InternalExpenseViewerPage
